import React, { useState, useCallback } from "react";
import { Bookmark } from "lucide-react";
import { motion, useAnimationControls } from "framer-motion";
import { musicClient } from "@/lib/musicApi";
import { useToast } from "@/contexts/ToastContext";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { MembersOnlySheet } from "@/components/common/MembersOnlySheet";
import { useTranslation } from "react-i18next";

// Single-tap save/unsave toggle to the unified saves collection. Toast on every
// action; spring burst on save, scale-down pulse on unsave.
export const SaveButton = ({ trackId, initialSaved = false, size = 20, onChange }) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(initialSaved);
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(false);
  const controls = useAnimationControls();
  const toast = useToast();
  const { isSubscriber } = usePlatformAuth();

  const toggle = useCallback(async (e) => {
    e.stopPropagation();
    if (!isSubscriber) { setUpsell(true); return; }
    if (busy) return;
    setBusy(true);
    const nextSaved = !saved;
    setSaved(nextSaved);
    if (nextSaved) {
      controls.start({ scale: [1, 1.3, 1], rotate: [0, 8, 0], transition: { type: "spring", stiffness: 400, damping: 15 } });
    } else {
      controls.start({ scale: [1, 0.85, 1], transition: { duration: 0.2 } });
    }
    try {
      const { data } = await musicClient.post(`/tracks/${trackId}/save`);
      const isSaved = data.state === "saved";
      setSaved(isSaved);
      toast.success(isSaved ? t("saves.saved") : t("saves.removed"));
      onChange && onChange(isSaved);
    } catch {
      setSaved(!nextSaved);
      toast.error(t("saves.error"));
    } finally {
      setBusy(false);
    }
  }, [saved, busy, controls, trackId, toast, onChange, isSubscriber]);

  return (
    <button onClick={toggle} data-testid={`save-btn-${trackId}`} title={t("saves.save")} aria-label={t("saves.save")} className="flex items-center">
      <motion.span animate={controls} style={{ display: "inline-flex" }}>
        <Bookmark size={size} style={{ color: saved ? "var(--accent-primary)" : "var(--text-secondary)" }} fill={saved ? "var(--accent-primary)" : "none"} />
      </motion.span>
      <MembersOnlySheet open={upsell} onClose={() => setUpsell(false)} />
    </button>
  );
};

export default SaveButton;
